/**
 * The center peek (1.I-2 deliverable 6): a modal dialog on the kit's `cx-center-peek` classes and
 * backdrop. Tab never leaves it (the kit's focus trap, skipped for a Tab the editor consumed);
 * a backdrop click closes it.
 */
import { useFocusTrap, useLayer, usePortalContainer, useReturnFocus } from '@codex/ui';
import { type KeyboardEvent, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { currentEntry } from './pane-history.js';
import { LazyPaneDocument } from './pane-lazy.js';
import { PeekHeader, usePaneTitle } from './peek-header.js';
import type { PeekProps } from './side-peek.js';

export function CenterPeek({ stack, onClose, onOpenAsPage }: PeekProps) {
  const entry = currentEntry(stack);
  const title = usePaneTitle(entry.docId);
  const ref = useRef<HTMLDivElement>(null);
  // A press that starts on the backdrop and ends there; closing on the click (after the browser
  // has moved focus) lets the returned focus stay where it was given.
  const pressed = useRef(false);
  // The token root is set at the first commit, long before a peek can open.
  const container = usePortalContainer() as HTMLElement;
  const trap = useFocusTrap(ref);
  useLayer(true, onClose, 'center-peek', { region: ref });
  useReturnFocus(true);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.defaultPrevented) return;
    trap(event);
  };
  return createPortal(
    // biome-ignore lint/a11y/noStaticElementInteractions: the backdrop's click is a pointer convenience; Esc is the keyboard path
    // biome-ignore lint/a11y/useKeyWithClickEvents: as above
    <div
      className="cx-center-peek__backdrop cx-nav-backdrop"
      onMouseDown={(event) => {
        pressed.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        const start = pressed.current;
        pressed.current = false;
        if (start && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        className="cx-center-peek cx-raised cx-nav-center"
        role="dialog"
        aria-modal="true"
        aria-label={`Center peek: ${title}`}
        data-testid="nav.peek.center"
        data-nav-pane="center"
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <PeekHeader
          title={title}
          stack={stack}
          history={false}
          closeLabel="Close peek"
          onBack={() => undefined}
          onForward={() => undefined}
          onOpenAsPage={onOpenAsPage}
          onClose={onClose}
        />
        <div className="cx-peek__body cx-nav-peek__body">
          <LazyPaneDocument docId={entry.docId} blockId={entry.blockId} source="center" />
        </div>
      </div>
    </div>,
    container,
  );
}
